import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { IonInput, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrderType } from 'src/smoothr-web-app-core/enums/OrderType';
import { AllowGpsModalResult } from '../../../app/components/allow-gps-modal/allow-gps-modal.component';
import { calculateGeoDistance, MapsUtils } from '../../utils/maps-leaflet';
import { MapsUtils as MapUtils2 } from 'src/smoothr-web-app-core/utils/maps-utils';

import { Api } from '../../../smoothr-web-app-core/api/api';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import Address from '../../../smoothr-web-app-core/models/Address';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { TimeUtils } from '../../../smoothr-web-app-core/utils/time-utils';
import { coverFlow, sleep, venueAcceptsOrders } from '../../../smoothr-web-app-core/utils/utils';

import { MapLocationModalComponent } from 'src/app/components/map-location-modal/map-location-modal.component';
import { OrderUtils } from 'src/smoothr-web-app-core/utils/order-utils';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import { MapPage } from 'src/app/pages/map/map.page';
import { FormControl } from '@angular/forms';
import { CheckDeliveryRadiusModalComponent } from 'src/app/components/check-delivery-radius-modal/check-delivery-radius-modal.component';
import Customer from 'src/smoothr-web-app-core/models/Customer';
import PromoCode from 'src/smoothr-web-app-core/models/PromoCode';
import { AnalyticsService } from 'src/smoothr-web-app-core/services/analytics/analytics.service';
import Fuse from 'fuse.js'; // if using modules
import { CobaltSdkService } from 'src/app/services/cobalt-sdk.service';

import * as leaflet from 'leaflet';
import '@maplibre/maplibre-gl-leaflet';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
	selector: 'app-map-venue',
	templateUrl: './map-venue.component.html',
	styleUrls: ['./map-venue.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapVenueComponent extends RepositoryDirective implements OnInit, OnDestroy {
	@Input() showSearchInput = false;
	@Input() showVenueList = true;
	@Input() venueToFirstPositionShortId = '';
	@ViewChild('mapContainer', { static: true })
	mapElement: ElementRef;
	@ViewChild('swiper') slides: ElementRef | undefined;
	map: leaflet.Map;
	@ViewChild(IonInput, { static: true })
	inputField: IonInput;
	@ViewChild('venueList', { static: false }) venueList;
	public searchRestaurant = false;
	public searchRestaurantControl = new FormControl('');
	mapOptions: leaflet.MapOptions = {
		center: { lat: 51.1657, lng: 10.4515 }, // Center of Germany
		zoom: 6,
		maxZoom: 15,
		minZoom: 5,
		layers: [
			leaflet.maplibreGL({
				style: 'https://tiles.openfreemap.org/styles/positron',
				maxTileCacheSize: 0, // Or low value like 10
			}),
		],
		zoomControl: false,
		preferCanvas: true,
		renderer: leaflet.canvas(),
		markerZoomAnimation: false,
	};
	clusterer: leaflet.MarkerClusterGroup;
	loading = false;
	loadingAddress = false;
	predictions: any[] = [];
	showHint = false;
	showNoVenueFound = false;
	searchTerm: string;
	allVenues: Venue[] = [];
	selectedVenueForDelivery: Venue;
	hasUserInteracted = false; // Flag to track if user has enabled geolocation or searched
	isMobile = window.innerWidth <= 992;
	showMap = !this.isMobile;

	@HostListener('window:resize', ['$event'])
	onResize(event: any) {
		const wasMobile = this.isMobile;
		this.isMobile = window.innerWidth <= 992;
		if (wasMobile && !this.isMobile) {
			this.showMap = true;
			this.cdr.detectChanges();
		}
	}

	toggleMap() {
		this.showMap = !this.showMap;
		this.cdr.detectChanges();
		this.invalidateMapAfterTransition();
	}

	// Map container transitions width over 1s. Trigger invalidation throughout the timeline so Leaflet
	// loads tiles for the new container size — without this the map renders gray after a programmatic flip.
	private invalidateMapAfterTransition() {
		const intervals = [100, 300, 500, 800, 1100];
		intervals.forEach(ms => {
			setTimeout(() => {
				if (this.map) {
					this.map.invalidateSize();
				}
				window.dispatchEvent(new Event('resize'));
			}, ms);
		});
	}

	slidesOpts = {
		slidesPerView: 1,
		coverflowEffect: {
			rotate: 0,
			stretch: 15,
			depth: 10,
			modifier: 2,
		},
		on: coverFlow,
	};
	selectedVenue: Venue;
	MapsUtils = MapsUtils;
	private onDestroy$ = new Subject<void>();
	public promoValue = '';
	isVenueExpanded = false;

	constructor(
		private snackbarCtrl: MatSnackBar,
		public repository: RepositoryService,
		private translate: TranslateService,
		private router: Router,
		private cdr: ChangeDetectorRef,
		private modalCtrl: ModalController,
		private route: ActivatedRoute,
		private analytics: AnalyticsService,
		private cobalt: CobaltSdkService
	) {
		super(repository);
	}

	relevantVenues: Venue[] = [];
	venuesOnSideBar: Venue[] = [];

	get showSideBar(): boolean {
		if (this.isMobile && !this.showMap) {
			return true;
		}
		return this.venuesOnSideBar.length > 0 || !!this.selectedVenue;
	}

	// Add trackBy function for better performance
	trackByVenue(index: number, venue: Venue): string {
		return venue._id;
	}

	static navigate(router: Router) {
		return router.navigateByUrl(MapPage.url);
	}

	ngAfterViewInit(): void {
		console.log('ngAfterViewInit()');
		// Initialize Leaflet map
		try {
			this.map = leaflet.map(this.mapElement.nativeElement, this.mapOptions);
			this.map.attributionControl.setPrefix('');
			setTimeout(function () {
				window.dispatchEvent(new Event('resize'));
			}, 1000);

			// Only load venues if user has interacted and address is available
			if (this.hasUserInteracted && this.address) {
				this.setupMap(this.relevantVenues, null);
			} else {
				// Don't show any venues by default
				this.setupMap([], null);
			}
		} catch (e) {
			console.error(e);
		}
	}

	ngOnInit() {
		console.log('ngOnInit()');
		super.ngOnInit();

		new Promise<void>(async resolve => {
			await this.fillInPlace(null);
			await this.initAutocomplete();
			resolve();
		});

		this.searchRestaurantControl.valueChanges
			.pipe(debounceTime(700), takeUntil(this.onDestroy$), distinctUntilChanged())
			.subscribe(async value => {
				// Mark user as interacted when they start searching
				if (value && value.trim()) {
					this.hasUserInteracted = true;
				}

				if (this.allVenues.length === 0) {
					await this.resetMap();
				}
				if (value) {
					const searchValue = value.trim().toLowerCase();

					// Step 1: Exact prefix matches (highest priority)
					// Matches where city, name, or postal code STARTS WITH the search term
					const exactPrefixMatches = this.allVenues.filter(venue => {
						const cityMatch = venue.city?.de?.toLowerCase().startsWith(searchValue);
						const nameMatch = venue.name?.toLowerCase().startsWith(searchValue);
						const postalCodeMatch = venue.postalCode?.startsWith(searchValue);
						const readableIdMatch = venue.readableId?.toLowerCase().startsWith(searchValue);
						return cityMatch || nameMatch || postalCodeMatch || readableIdMatch;
					});

					// Step 2: Exact contains matches (medium priority)
					// Matches where the search term appears exactly (not fuzzy) in any field
					const exactContainsMatches = this.allVenues.filter(venue => {
						const cityMatch = venue.city?.de?.toLowerCase().includes(searchValue);
						const nameMatch = venue.name?.toLowerCase().includes(searchValue);
						const streetMatch = venue.street?.toLowerCase().includes(searchValue);
						const postalCodeMatch = venue.postalCode?.includes(searchValue);
						return cityMatch || nameMatch || streetMatch || postalCodeMatch;
					});

					// Step 3: Fuzzy matches (lowest priority) - only if not enough exact matches
					let fuzzyMatches: Venue[] = [];
					if (exactPrefixMatches.length < 5) {
						const fuseOptions = {
							keys: ['name', 'readableId', 'city.de', 'postalCode', 'street'],
							threshold: 0.2, // More strict threshold (was 0.3)
							includeScore: true,
							ignoreLocation: true,
							minMatchCharLength: Math.min(searchValue.length, 3),
						};
						const fuse = new Fuse(this.allVenues, fuseOptions);
						const fuseResults = fuse.search(value);
						fuzzyMatches = fuseResults
							.filter(r => (r.score ?? 1) < 0.2) // Only very close matches
							.map(r => r.item);
					}

					// Combine results with priority: prefix > contains > fuzzy
					// Use Set to avoid duplicates
					const combinedResults = new Set<Venue>([
						...exactPrefixMatches,
						...exactContainsMatches.filter(v => !exactPrefixMatches.includes(v)),
						...fuzzyMatches.filter(v => !exactPrefixMatches.includes(v) && !exactContainsMatches.includes(v)),
					]);

					this.relevantVenues = Array.from(combinedResults);

					let isNoResults = false;
					if (this.relevantVenues.length === 0) {
						isNoResults = true;
						this.relevantVenues = this.allVenues;
					}

					// Sort by distance if user address is available
					if (this.address && this.relevantVenues.length > 0) {
						this.relevantVenues.sort((a, b) => {
							const distA = calculateGeoDistance(
								this.address.lat, this.address.lng,
								a.location.coordinates[1], a.location.coordinates[0]
							);
							const distB = calculateGeoDistance(
								this.address.lat, this.address.lng,
								b.location.coordinates[1], b.location.coordinates[0]
							);
							return distA - distB;
						});
					}

					this.venuesOnSideBar = isNoResults ? [] : this.relevantVenues;
					this.showNoVenueFound = isNoResults;

					// On mobile, the list view hides the map, so the "check the map"
					// hint is useless until we flip to map view automatically.
					if (isNoResults && this.isMobile && !this.showMap) {
						this.showMap = true;
						this.invalidateMapAfterTransition();
					}
				} else {
					// Only show all venues if user has enabled geolocation
					this.relevantVenues = this.hasUserInteracted ? this.allVenues : [];
					this.venuesOnSideBar = [];
					this.showNoVenueFound = false;
				}
				await this.setupMap(this.relevantVenues, null);
			});
		console.log('PARAMS');
		this.promoValue = '';

		this.route.params.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
			if (params?.code && this.router?.url?.includes('promo-code')) {
				console.log('params code', params.code);
				this.promoValue = params?.code;
			} else {
				this.promoValue = '';
			}
		});
		this.askAboutPosition();
	}

	ngOnDestroy(): void {
		console.log('ngOnDestroy()');
		this.onDestroy$.next();
	}

	async askAboutPosition() {
		console.log('askAboutPosition()');
		try {
			const result = await MapLocationModalComponent.show(this.modalCtrl);
			if (result !== AllowGpsModalResult.ALLOW) {
				// User denied GPS - don't show any venues
				this.hasUserInteracted = false;
				await this.setupMap([], null);
				return;
			}

			// Mark user as interacted when they allow geolocation
			this.hasUserInteracted = true;

			// Check if we already have a stored address first
			if (this.address && this.address.city) {
				// Use existing address and populate search bar
				this.searchRestaurantControl.patchValue(this.address.city);
				return;
			}

			// Try to get fresh location if no stored address
			const position = await new Promise<GeolocationPosition>((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					timeout: 10000, // Even shorter timeout
					maximumAge: 600000, // Use cached location up to 10 minutes
					enableHighAccuracy: false,
				});
			});

			// Convert position to address using Google Maps geocoding
			const address = await this.geocodePosition(position);
			if (!address) {
				await this.setupMap([], null);
				return;
			}
			this.repository.address.emit(address);
			if (address.city) {
				this.searchRestaurantControl.patchValue(address.city);
			}
		} catch (e) {
			console.error(e);
			// User denied or error occurred - don't show any venues
			this.hasUserInteracted = false;
			await this.setupMap([], null);
		}
	}

	private async geocodePosition(position: GeolocationPosition): Promise<Address> {
		console.log('geocodePosition()');
		return new Promise<Address>((resolve, reject) => {
			const geocoder = new google.maps.Geocoder();
			geocoder.geocode(
				{
					location: new google.maps.LatLng(position.coords.latitude, position.coords.longitude),
				},
				(results, status) => {
					if (status === google.maps.GeocoderStatus.OK && results?.length > 0) {
						resolve(MapUtils2.placeToAddress(results[0]));
					} else {
						reject(new Error(`Geocoding failed: ${status}`));
					}
				}
			);
		});
	}

	async resetMap() {
		console.log('resetMap()', this.allVenues.length);
		// Only load venues if user has interacted
		if (!this.hasUserInteracted) {
			console.log('User has not interacted yet, not loading venues');
			await this.setupMap([], null);
			return;
		}
		if (this.allVenues.length === 0) {
			try {
				this.allVenues = (
					await this.cobalt.sdk.query.mapsPoiVenues({ customerGroup: environment.customerGroup })(s => s.$all({ cyclic: {} }))
				).map(ven => {
					try {
						ven.openingHours = TimeUtils.sanitizeHours(ven.openingHours as any, (ven.specialOpeningHours ?? []) as any) as any;
						ven.deliveryHours = TimeUtils.sanitizeHours(
							ven.deliveryHours as any,
							((ven as any).specialDeliveryHours ?? []) as any
						) as any;
					} catch (e) {
						console.error({
							message: 'Error while sanitizing hours ' + e,
							venue: ven.name + ' ' + ven.readableId,
							openingHours: ven.openingHours,
							deliveryHours: ven.deliveryHours,
						});
					}
					return ven;
				}) as unknown as Venue[];

				if (environment.sortMapVenue) {
					this.allVenues = this.allVenues.sort((a, b) => {
						const nameA = a?.name?.replace(environment.sortMapVenue, '')?.trim();
						const nameB = b?.name?.replace(environment.sortMapVenue, '')?.trim();
						return nameA?.localeCompare(nameB);
					});
				}
				if (this.address) {
					this.allVenues = this.allVenues.sort((a, b) => {
						return (
							calculateGeoDistance(this.address.lat, this.address.lng, a.location.coordinates[1], a.location.coordinates[0]) -
							calculateGeoDistance(this.address.lat, this.address.lng, b.location.coordinates[1], b.location.coordinates[0])
						);
					});
				}
			} catch (e) {
				console.error(e);
			}
		}
		await this.setupMap(this.allVenues, null);
	}

	async setupMap(relevantVenues: Venue[], selectedVenue: Venue) {
		console.log('setupMap()', selectedVenue?.name);
		if (this.loading) {
			return;
		}
		// Ensure map is initialized before trying to add venues
		if (!this.map) {
			console.log('Map not initialized yet, skipping setupMap');
			return;
		}

		// Only display venues if user has interacted (enabled geolocation or searched)
		if (!this.hasUserInteracted) {
			console.log('User has not interacted yet, not displaying venues');
			relevantVenues = [];
			selectedVenue = null;
		}

		if (this.isMobile && selectedVenue && !this.showMap) {
			this.showMap = true;
			this.invalidateMapAfterTransition();
		}

		this.relevantVenues = relevantVenues || [];
		this.selectedVenue = selectedVenue;
		// this.loading = true;
		this.cdr.markForCheck();

		const scrollList = async (venue: Venue) => {
			if (venue && venue.location && venue.location.coordinates) {
				try {
					const selectedIndex = this.relevantVenues.findIndex(ven => ven._id === venue._id) ?? 0;
					// wait until slides rendered
					this.swipeIndex = selectedIndex;
					await this.scrollToVenue(selectedIndex);
				} catch (e) {}
			}
		};

		this.clusterer = MapsUtils.addVenuesToMap(this.clusterer, selectedVenue, this.relevantVenues, this.map, async venue => {
			this.selectedVenue = venue;
			this.cdr.markForCheck();
			MapsUtils.zoomToSelectedVenue(this.map, venue);
			await scrollList(venue);
		});

		if (this.selectedVenue && this.selectedVenue.location && this.selectedVenue.location.coordinates) {
			MapsUtils.zoomToSelectedVenue(this.map, this.selectedVenue);
			await scrollList(this.selectedVenue);
		}

		// this.loading = false;
		this.cdr.markForCheck();
	}

	async executeSearch() {
		console.log('executeSearch()');
		this.loading = true;
		try {
			const result = await MapUtils2.executeSearch(this.inputField, ['postal_code']);
			await this.fillInPlace(result);
		} catch (e) {}
		this.loading = false;
	}

	resetSearch() {
		console.log('resetSearch()');
		this.searchTerm = '';
		this.predictions = [];
		this.selectedVenueForDelivery = null;
		this.selectedVenue = null;
		if (this.address) {
			this.repository.address.emit(null);
		}
		this.showHint = false;
		// Only show venues if user has enabled geolocation
		if (this.hasUserInteracted && this.address) {
			this.relevantVenues = this.allVenues;
		} else {
			this.relevantVenues = [];
		}
		this.cdr.markForCheck();
	}

	async fillInPlace(address: Address) {
		console.log('fillInPlace()', address);
		if (!address) {
			return;
		}
		if (!address?.lat) {
			try {
				const result = await MapUtils2.getPlaceByPred(address as any);
				console.log('RESUlt of mapping', result);
				address = MapUtils2.placeToAddress(result as any);
			} catch (e) {
				this.loading = false;
				return;
			}
		}
		this.loading = true;
		try {
			this.searchTerm = MapsUtils.checkAddress(address);
			this.loading = false;
			if (this.address !== address) {
				this.repository.address.emit(address);
			}
			// await this.onSlideChange();
		} catch (e) {
			console.error(e);
			this.snackbarCtrl.open(this.translate.instant(e), null, {
				duration: 2000,
			});
		}
		this.loading = false;
	}

	async loadVenues(address: Address) {
		console.log('loadVenues()', address);
		if (this.loading) {
			return;
		}
		this.loading = true;
		this.cdr.markForCheck();

		try {
			this.relevantVenues =
				((
					await this.cobalt.sdk.query.mapsPoiVenues({
						customerGroup: environment.customerGroup,
						filter: {
							postalCode: address.postalCode,

							lat: address.lat,
							lng: address.lng,
							distance: 10,
						},
					})(s => s.$all({ cyclic: {} }))
				).map(ven => {
					try {
						ven.openingHours = TimeUtils.sanitizeHours(ven.openingHours as any, (ven.specialOpeningHours ?? []) as any) as any;
						ven.deliveryHours = TimeUtils.sanitizeHours(
							ven.deliveryHours as any,
							((ven as any).specialDeliveryHours ?? []) as any
						) as any;
					} catch (e) {
						console.error({
							message: 'Error while sanitizing hours ' + e,
							venue: ven.name + ' ' + ven.readableId,
							openingHours: ven.openingHours,
							deliveryHours: ven.deliveryHours,
						});
					}
					return ven;
				}) as unknown as Venue[]) || [];

			this.searchTerm = MapsUtils.addressToString(address);
			if (this.relevantVenues.length === 0) {
				this.snackbarCtrl.open(
					this.translate.instant('map_page.no_venues_in_address', {
						title: this.searchTerm,
					}),
					null,
					{
						duration: 2000,
					}
				);
				this.loading = false;
				await this.resetMap();
				this.cdr.markForCheck();
				return;
			}
			if (this.selectedVenueForDelivery) {
				const deliveryVenues = this.relevantVenues.filter(ven => venueAcceptsOrders(ven, PreorderType.DELIVERY));
				const selectedDeliveryVenue = deliveryVenues.find(ven => ven._id === this.selectedVenueForDelivery._id);
				if (deliveryVenues.length > 0) {
					this.relevantVenues = deliveryVenues;
					this.setupMap(this.relevantVenues, null);
				}
				// console.log('selected Delivery', selectedDeliveryVenue, deliveryVenues)
				// if (selectedDeliveryVenue) {
				// 	await this.selectVenue(selectedDeliveryVenue, PreorderType.DELIVERY);
				// } else {
				// 	const choice = await DeliveryNotAvailableModalComponent.show(this.modalCtrl);
				// 	switch (choice) {
				// 		case DeliveryNotAvailableAction.DECLINE:
				// 			this.localVenues = deliveryVenues;
				// 			if (deliveryVenues.length === 0) {
				// 				this.repository.address.emit(null);
				// 			} else {
				// 				this.showHint = true;
				// 			}
				// 			await this.setupMap(null);
				// 			break;
				// 		case DeliveryNotAvailableAction.TAKE_AWAY:
				// 			await this.selectVenue(this.selectedVenueForDelivery, PreorderType.TAKE_AWAY);
				// 			break;
				// 	}
				// // }
				this.selectedVenueForDelivery = null;
				return;
			}
			this.selectedVenue = null;
			this.loading = false;
			// If map is not ready yet, setupMap will return early
			// It will be called again in ngAfterViewInit when map is ready
			await this.setupMap(this.relevantVenues, this.selectedVenue);
		} catch (e) {
			console.error(e);
		}
		this.loading = false;
		this.cdr.markForCheck();
	}

	async selectVenue(venue: Venue, preorderType: PreorderType, attempt = 0, e?: any) {
		if (this.loading) {
			return;
		}
		this.loading = true;
		let promoCode: PromoCode = null;

		if (preorderType === PreorderType.DELIVERY) {
			// Check delivery radius first for delivery orders
			if (venue.distance > venue.deliveryRadius && venue.deliveryByRadius && !venue.isPostalDelivery) {
				this.snackbarCtrl.open(this.translate.instant('map_page.venue_does_not_delivery_to_address'), null, {
					duration: 2000,
				});
				this.loading = false;
				this.repository.address.next(null);
				return;
			}

			// Always show address modal for delivery, regardless of stored address
			const result: { venue: Venue; address: Address } = await CheckDeliveryRadiusModalComponent.show(
				this.modalCtrl,
				null,
				this.address
			);
			console.log(result);
			if (result?.venue && result?.address) {
				this.repository.address.emit(result.address);

				// Create order immediately for instant navigation
				this.repository.createOrder(result.venue, result.address, OrderType.PREORDER, PreorderType.DELIVERY);

				// Navigate immediately - menu page skeleton will handle loading
				await this.router.navigateByUrl('/menu', {
					replaceUrl: true,
				});

				// Handle promo code and venue data loading after navigation (non-blocking)
				if (this.promoValue) {
					promoCode = await this.checkPromoCode(this.promoValue, result.venue, this.customer, PreorderType.DELIVERY);
				}

				// Load venue data in background (menu page skeleton handles UX)
				await this.repository.getVenue(result.venue._id);

				if (promoCode) {
					const orderValue = OrderUtils.applyPromo(
						this.translate,
						this.repository._venue,
						this.repository._order,
						promoCode,
						this.analytics
					) as any;
					orderValue.customerRedemptionPromoCode = this.promoValue;
					this.repository.onOrderChange(orderValue);
				}

				this.loading = false;
				this.cdr.detectChanges();
				return;
			} else {
				this.loading = false;
				this.cdr.detectChanges();
				return;
			}
		}

		// Handle non-delivery preorder types (TAKE_AWAY, INSIDE, PARK_COLLECT)
		try {
			// Create order immediately for instant navigation
			this.repository.createOrder(venue, this.address, OrderType.PREORDER, preorderType);

			// Navigate immediately - menu page skeleton will handle loading
			this.router.navigateByUrl('/menu', {
				replaceUrl: true,
			});

			// Handle promo code and venue data loading after navigation (non-blocking)
			if (this.promoValue) {
				promoCode = await this.checkPromoCode(this.promoValue, venue, this.customer, preorderType);
			}

			// Load venue data in background (menu page skeleton handles UX)
			await this.repository.getVenue(venue._id);

			if (promoCode) {
				const orderValue = OrderUtils.applyPromo(
					this.translate,
					this.repository._venue,
					this.repository._order,
					promoCode,
					this.analytics
				) as any;
				orderValue.customerRedemptionPromoCode = this.promoValue;
				this.repository.onOrderChange(orderValue);
			}
		} catch (e) {
			await this.selectVenue(venue, preorderType, attempt + 1, e);
		}
	}

	async checkPromoCode(promoValue: string, venue: Venue, customer: Customer, preorderType: PreorderType) {
		console.log('checkPromoCode()', promoValue, venue, customer, preorderType);
		try {
			const promoCode = (
				await Api.getPromoCode({
					code: promoValue,
					venue: venue._id,
					customer: customer._id,
				})
			).data;
			promoCode.customerRedemptionPromoCode = promoValue;
			return promoCode;
		} catch (e) {
			return null;
		}
	}

	async initAutocomplete() {
		console.log('initAutocomplete()');
		try {
			await MapUtils2.initAutocomplete(
				this.inputField,
				predictions => {
					console.log('PREV', predictions);
					this.predictions = predictions as any[];
					this.cdr.detectChanges();
				},
				['postal_code']
				// loading => (this.loadingAddress = loading)
			);
		} catch (e) {
			console.log(e);
		}

		this.loading = false;
	}

	async loadPlace(pred: any) {
		console.log('loadPlace()', pred);
		if (this.loading) {
			return;
		}
		this.loading = true;
		try {
			const result = await MapUtils2.executeSearch(this.inputField, ['postal_code']);
			await this.fillInPlace(result);
		} catch (e) {
			await this.fillInPlace(pred);
		}

		this.loading = false;
		this.cdr.detectChanges();

		// if (this.loading) {
		// 	return;
		// }
		// this.loading = true;
		// await this.fillInPlace(pred);
		// this.loading = false;
		// this.cdr.detectChanges();
	}

	onAddress() {
		super.onAddress();
		new Promise(async () => {
			if (!this.address) {
				await this.resetMap();
				return;
			}
			// Populate search bar with city name when address is available (e.g., after page refresh)
			if (this.address.city && this.searchRestaurantControl.value !== this.address.city) {
				this.searchRestaurantControl.patchValue(this.address.city);
			}
			// Only load venues if user has interacted
			if (this.hasUserInteracted) {
				await this.loadVenues(this.address);
			} else {
				await this.setupMap([], null);
			}
		});
	}

	async checkAddressDelivery(venue: Venue, address: Address) {
		if (
			// OrderUtils.isDelivery(this.order) &&
			this.address.number &&
			this.address.city &&
			this.address.postalCode &&
			this.address.street
		) {
			try {
				const addressValidationResult = ValidationUtils.validateAddress(address, true);
				if (addressValidationResult) {
					console.log('not valid adrress', addressValidationResult);
					// await InfoModalComponent.show(this.modalCtrl, {
					// 	info: this.translate.instant(
					// 		'map_page.venue_does_not_delivery_to_address'
					// 	),
					// 	closeButton: this.translate.instant('add_points.close')
					// });
					return false;
				}
				if (venue.deliveryByAreas && venue?.deliveryAreas?.length > 0) {
					return true;
				}
				console.log(address.lat, address.lng, venue.location.coordinates[1], venue.location.coordinates[0]);
				const delivers =
					(venue.deliveryByRadius &&
						calculateGeoDistance(address.lat, address.lng, venue.location.coordinates[1], venue.location.coordinates[0]) <=
							venue.deliveryRadius) ||
					(venue.isPostalDelivery && venue.deliveryPostalCodes.indexOf(address.postalCode));
				if (!delivers) {
					// this.orderForm.controls['street'].setErrors({street_delivery: true});

					this.snackbarCtrl.open(this.translate.instant('errors.street_delivery'), null, {
						duration: 2000,
					});
					return false;
				}
				// this.repository.address.next(address);
				// this.orderForm.patchValue({
				// 	street: this.address.street,
				// 	number: this.address.number,
				// 	postalCode: this.address.postalCode,
				// 	city: this.address.city
				// });
				return true;
			} catch (e) {
				console.log(e);
				// this.orderForm.controls['street'].setErrors({street_not_found: true});
				// this.snackbarCtrl.open(
				// 	this.translate.instant('errors.street_not_found'),
				// 	null,
				// 	{
				// 		duration: 2000
				// 	}
				// );
				return false;
			}
		} else {
			return false;
		}
	}

	private async scrollToVenue(index: number) {
		try {
			await this.venueList.scrollToPoint(0, this.venueList.el.children[index].offsetTop - 250, 1000);
		} catch (e) {}
	}

	cleanValue() {
		this.searchRestaurantControl.patchValue('');
		// If user hasn't enabled geolocation, don't show any venues when clearing search
		if (this.hasUserInteracted) {
			this.relevantVenues = this.allVenues;
		} else {
			this.relevantVenues = [];
		}
		this.setupMap(this.relevantVenues, null);
	}

	swipeIndex = 0;
	async swipeToIndex(index: number) {
		// alert(index + ' ' + this.relevantVenues.length);
		if (index >= this.relevantVenues.length) {
			index = 0;
		}
		this.swipeIndex = index;
		this.selectedVenue = this.relevantVenues[index];
		MapsUtils.zoomToSelectedVenue(this.map, this.selectedVenue);
		await this.scrollToVenue(index);
		// await this.slides.nativeElement.swiper.slideTo(index);
		// await this.setupMap(this.relevantVenues[index]);
	}

	onVenueExpansionChange(isExpanded: boolean) {
		this.isVenueExpanded = isExpanded;
		this.cdr.markForCheck();
	}
}
